import * as React from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import {
  Button,
  Checkbox,
  colors,
  Link,
  Menu,
  MenuItem,
  TextField,
  ListItemText
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Column } from 'material-table';
import { DateInput } from 'components/DatePicker';
import { CalendarPeriod, DateChange, parsePeriodDate } from 'components/DatePicker/utils/constants';
import AlteredMaterialTable from 'components/MaterialTable';
import { useDebounceCallback } from 'hooks/useDebouncedCallback';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from 'redux/rootReducer';
import * as monthlyReportsDispatch from 'redux/ducks/monthlyReports';
import * as monthlyReportRecipientsDispatch from 'redux/ducks/monthlyReportRecipients';
import * as notificationDispatch from 'redux/ducks/notification';
import {
  selectMonthlyReportsData,
  selectMonthlyReportsHiddenColumns,
  selectMonthlyReportsStatus,
  selectMonthlyReportsTotal
} from 'redux/ducks/monthlyReports/selectors';
import {
  MonthlyReport,
  MonthlyReportsActions,
  MonthlyReportsQueryParams,
  MonthlyReportRow
} from 'redux/ducks/monthlyReports';
import { ErrorActions } from 'redux/ducks/error';
import { NotificationActions } from 'redux/ducks/notification';
import {
  MonthlyReportRecipient,
  MonthlyReportRecipientActions
} from 'redux/ducks/monthlyReportRecipients/types';
import DataModal from './components/DataModal';
import RecipientsModal from './components/RecipientsModal';

type MonthlyReportTableColumn = Column<MonthlyReport> & {
  sortField?: string;
  columnKey?: AdminColumnKey;
};

type AdminColumnKey =
  'accountId' | 'accountName' | 'partner' | 'date' | 'file' | 'data' | 'template' | 'recipients';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type MonthlyReportsTableProps = StateProps & DispatchProps;
type FilterKey = 'accounts' | 'accountName' | 'partner';

type MonthlyReportsTableState = {
  selectedDataReport?: MonthlyReport;
  selectedRecipientsReport?: MonthlyReport;
  page: number;
  pageSize: number;
  sortColumnIndex?: number;
  sortDirection?: 'asc' | 'desc';
  selectedMonth: DateChange;
  debouncedAccountsFilter: string;
  debouncedAccountNameFilter: string;
  debouncedPartnerFilter: string;
  sendingReportIds: string[];
  recipientsMutationPending: boolean;
  columnsMenuAnchor?: HTMLElement;
};

type MonthlyReportsTableAction =
  | { type: 'open_data_modal'; payload: MonthlyReport }
  | { type: 'close_data_modal' }
  | { type: 'open_recipients_modal'; payload: MonthlyReport }
  | { type: 'close_recipients_modal' }
  | { type: 'set_page'; payload: number }
  | { type: 'set_page_size'; payload: number }
  | {
      type: 'set_sort';
      payload: { columnIndex?: number; direction?: 'asc' | 'desc' };
    }
  | { type: 'set_selected_month'; payload: DateChange }
  | { type: 'set_debounced_filter'; payload: { key: FilterKey; value: string } }
  | { type: 'set_report_sending'; payload: { reportId: string; isSending: boolean } }
  | { type: 'set_recipients_mutation_pending'; payload: boolean }
  | { type: 'set_columns_menu_anchor'; payload?: HTMLElement };

const materialTableOptions = {
  toolbar: false,
  actionsColumnIndex: -1,
  showTitle: false,
  search: false,
  emptyRowsWhenPaging: false,
  paginationType: 'stepped',
  headerStyle: {
    backgroundColor: colors.grey['50']
  },
  rowStyle: {
    borderBottom: `1px solid ${colors.grey['200']}`
  },
  pageSize: 25,
  pageSizeOptions: [10, 15, 20, 25]
};

const FILTER_BORDER_COLOR = '#aad8d6';
const FILTER_BORDER_COLOR_ACTIVE = '#008b87';

const COLLAPSIBLE_COLUMNS: Array<{
  key: AdminColumnKey;
  labelId: string;
  defaultMessage: string;
}> = [
  { key: 'accountId', labelId: 'accountId', defaultMessage: 'Account ID' },
  {
    key: 'accountName',
    labelId: 'admin.monthlyReports.accountName',
    defaultMessage: 'Account name'
  },
  { key: 'partner', labelId: 'partner', defaultMessage: 'Partner' },
  { key: 'date', labelId: 'date', defaultMessage: 'Date' },
  { key: 'file', labelId: 'file', defaultMessage: 'File' },
  { key: 'data', labelId: 'admin.monthlyReports.input', defaultMessage: 'Input' },
  { key: 'template', labelId: 'monthlyReportTemplate', defaultMessage: 'Report template' },
  { key: 'recipients', labelId: 'recipientsMultiple', defaultMessage: 'Recipient(s)' }
];

const useStyles = makeStyles(() => ({
  tableFilterRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 12
  },
  columnsButton: {
    height: 40,
    minHeight: 40,
    boxSizing: 'border-box',
    borderColor: FILTER_BORDER_COLOR,
    '&:hover': {
      borderColor: FILTER_BORDER_COLOR_ACTIVE
    }
  },
  monthFilterInput: {
    minWidth: 200,
    '& .MuiButtonGroup-root': {
      height: 40
    },
    '& .MuiButtonBase-root': {
      height: 40,
      minHeight: 40,
      boxSizing: 'border-box'
    }
  },
  accountNameFilterInput: {
    minWidth: 240,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: FILTER_BORDER_COLOR
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: FILTER_BORDER_COLOR_ACTIVE
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: FILTER_BORDER_COLOR_ACTIVE
    }
  },
  partnerFilterInput: {
    minWidth: 220,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: FILTER_BORDER_COLOR
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: FILTER_BORDER_COLOR_ACTIVE
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: FILTER_BORDER_COLOR_ACTIVE
    }
  },
  accountFilterInput: {
    minWidth: 240,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: FILTER_BORDER_COLOR
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: FILTER_BORDER_COLOR_ACTIVE
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: FILTER_BORDER_COLOR_ACTIVE
    }
  }
}));

const getPartnerName = (report: MonthlyReport): string => report.partner ?? '-';

const getInitialState = (): MonthlyReportsTableState => ({
  page: 0,
  pageSize: materialTableOptions.pageSize,
  selectedMonth: parsePeriodDate({
    date: moment().subtract(1, 'month'),
    period: CalendarPeriod.month,
    isoWeek: true
  }),
  debouncedAccountsFilter: '',
  debouncedAccountNameFilter: '',
  debouncedPartnerFilter: '',
  recipientsMutationPending: false,
  sendingReportIds: []
});

const monthlyReportsTableReducer = (
  state: MonthlyReportsTableState,
  action: MonthlyReportsTableAction
): MonthlyReportsTableState => {
  if (action.type === 'open_data_modal') {
    return {
      ...state,
      selectedDataReport: action.payload
    };
  }

  if (action.type === 'close_data_modal') {
    const { selectedDataReport, ...nextState } = state;
    void selectedDataReport;
    return nextState;
  }

  if (action.type === 'open_recipients_modal') {
    return {
      ...state,
      selectedRecipientsReport: action.payload
    };
  }

  if (action.type === 'close_recipients_modal') {
    const { selectedRecipientsReport, ...nextState } = state;
    void selectedRecipientsReport;
    return nextState;
  }

  if (action.type === 'set_page') {
    return {
      ...state,
      page: action.payload
    };
  }

  if (action.type === 'set_page_size') {
    return {
      ...state,
      pageSize: action.payload,
      page: 0
    };
  }

  if (action.type === 'set_sort') {
    return {
      ...state,
      sortColumnIndex: action.payload.columnIndex,
      sortDirection: action.payload.direction,
      page: 0
    };
  }

  if (action.type === 'set_selected_month') {
    return {
      ...state,
      selectedMonth: action.payload,
      page: 0
    };
  }

  if (action.type === 'set_debounced_filter') {
    if (action.payload.key === 'accounts') {
      return {
        ...state,
        debouncedAccountsFilter: action.payload.value,
        page: 0
      };
    }

    if (action.payload.key === 'accountName') {
      return {
        ...state,
        debouncedAccountNameFilter: action.payload.value,
        page: 0
      };
    }

    return {
      ...state,
      debouncedPartnerFilter: action.payload.value,
      page: 0
    };
  }

  if (action.type === 'set_report_sending') {
    if (action.payload.isSending) {
      if (state.sendingReportIds.includes(action.payload.reportId)) {
        return state;
      }

      return {
        ...state,
        sendingReportIds: [...state.sendingReportIds, action.payload.reportId]
      };
    }

    return {
      ...state,
      sendingReportIds: state.sendingReportIds.filter((id) => id !== action.payload.reportId)
    };
  }

  if (action.type === 'set_recipients_mutation_pending') {
    return {
      ...state,
      recipientsMutationPending: action.payload
    };
  }

  if (action.type === 'set_columns_menu_anchor') {
    if (!action.payload) {
      const { columnsMenuAnchor, ...nextState } = state;
      void columnsMenuAnchor;
      return nextState;
    }

    return {
      ...state,
      columnsMenuAnchor: action.payload
    };
  }

  return state;
};

const MonthlyReportsTable: React.FC<MonthlyReportsTableProps> = (props) => {
  const classes = useStyles();
  const intl = useIntl();
  const {
    getReports,
    hiddenColumns,
    reports,
    reportsStatus,
    sendReportEmail,
    createRecipient,
    deleteRecipient,
    setHiddenColumns,
    showNotification,
    totalReports
  } = props;
  const [state, dispatchState] = React.useReducer(
    monthlyReportsTableReducer,
    undefined,
    getInitialState
  );
  const {
    selectedDataReport,
    selectedRecipientsReport,
    page,
    pageSize,
    sortColumnIndex,
    sortDirection,
    selectedMonth,
    debouncedAccountsFilter,
    debouncedAccountNameFilter,
    debouncedPartnerFilter,
    sendingReportIds,
    recipientsMutationPending,
    columnsMenuAnchor
  } = state;

  const openDataModal = React.useCallback((report: MonthlyReport) => {
    dispatchState({ type: 'open_data_modal', payload: report });
  }, []);

  const closeDataModal = React.useCallback(() => {
    dispatchState({ type: 'close_data_modal' });
  }, []);

  const openRecipientsModal = React.useCallback((report: MonthlyReport) => {
    dispatchState({ type: 'open_recipients_modal', payload: report });
  }, []);

  const closeRecipientsModal = React.useCallback(() => {
    dispatchState({ type: 'close_recipients_modal' });
  }, []);

  const setDebouncedAccountsFilter = useDebounceCallback((value: string) => {
    dispatchState({
      type: 'set_debounced_filter',
      payload: { key: 'accounts', value }
    });
  }, 400);

  const setDebouncedAccountNameFilter = useDebounceCallback((value: string) => {
    dispatchState({
      type: 'set_debounced_filter',
      payload: { key: 'accountName', value }
    });
  }, 400);

  const setDebouncedPartnerFilter = useDebounceCallback((value: string) => {
    dispatchState({
      type: 'set_debounced_filter',
      payload: { key: 'partner', value }
    });
  }, 400);

  const accountIdFilterParam = React.useMemo(
    () => debouncedAccountsFilter.trim(),
    [debouncedAccountsFilter]
  );

  const accountNamesFilterParam = React.useMemo(
    () => debouncedAccountNameFilter.trim(),
    [debouncedAccountNameFilter]
  );

  const partnersFilterParam = React.useMemo(
    () => debouncedPartnerFilter.trim(),
    [debouncedPartnerFilter]
  );

  const isColumnHidden = React.useCallback(
    (columnKey: AdminColumnKey) => hiddenColumns.includes(columnKey),
    [hiddenColumns]
  );

  const toggleColumn = React.useCallback(
    (columnKey: AdminColumnKey) => {
      if (hiddenColumns.includes(columnKey)) {
        setHiddenColumns(hiddenColumns.filter((key) => key !== columnKey));
        return;
      }

      setHiddenColumns([...hiddenColumns, columnKey]);
    },
    [hiddenColumns, setHiddenColumns]
  );

  const columns = React.useMemo<MonthlyReportTableColumn[]>(
    () => [
      {
        columnKey: 'date',
        sortField: 'date',
        title: intl.formatMessage({ id: 'date' }),
        field: 'date',
        sorting: true,
        hidden: isColumnHidden('date'),
        editable: 'never',
        render: (row) =>
          row.date
            ? intl.formatDate(row.date, {
                year: 'numeric',
                month: 'long'
              })
            : '-'
      },
      {
        columnKey: 'accountId',
        sortField: 'accountId',
        title: intl.formatMessage({ id: 'accountId', defaultMessage: 'Account ID' }),
        field: 'account',
        sorting: true,
        hidden: isColumnHidden('accountId'),
        editable: 'never',
        render: (row) => row.account?.id || '-'
      },
      {
        columnKey: 'accountName',
        sortField: 'accountName',
        title: intl.formatMessage({ id: 'admin.monthlyReports.accountName' }),
        field: 'account',
        sorting: true,
        hidden: isColumnHidden('accountName'),
        editable: 'never',
        render: (row) => row.account?.name || '-'
      },
      {
        columnKey: 'partner',
        sortField: 'partner',
        title: intl.formatMessage({ id: 'partner', defaultMessage: 'Partner' }),
        field: 'partner',
        sorting: true,
        hidden: isColumnHidden('partner'),
        editable: 'never',
        render: (row) => getPartnerName(row)
      },
      {
        columnKey: 'template',
        sortField: 'template',
        title: intl.formatMessage({ id: 'monthlyReportTemplate' }),
        field: 'template',
        sorting: true,
        hidden: isColumnHidden('template'),
        editable: 'never',
        render: (row) => row.template?.name || '-'
      },
      {
        columnKey: 'file',
        title: intl.formatMessage({ id: 'file', defaultMessage: 'File' }),
        field: 'reports',
        sorting: false,
        hidden: isColumnHidden('file'),
        editable: 'never',
        render: (row) => {
          const fileUrl = row.fileUrl;
          if (!fileUrl) {
            return '-';
          }

          return (
            <Link href={fileUrl} target='_blank' rel='noopener noreferrer'>
              {intl.formatMessage({ id: 'base.view', defaultMessage: 'View' })}
            </Link>
          );
        }
      },
      {
        columnKey: 'data',
        title: intl.formatMessage({ id: 'admin.monthlyReports.input' }),
        field: 'data',
        sorting: false,
        hidden: isColumnHidden('data'),
        editable: 'never',
        render: (row) => (
          <Button
            variant='outlined'
            color='primary'
            size='small'
            onClick={() => openDataModal(row)}
          >
            {intl.formatMessage({ id: 'base.view', defaultMessage: 'View' })}
          </Button>
        )
      },
      {
        columnKey: 'recipients',
        title: intl.formatMessage({ id: 'recipientsMultiple', defaultMessage: 'Recipient(s)' }),
        field: 'reports',
        sorting: false,
        hidden: isColumnHidden('recipients'),
        editable: 'never',
        render: (row) => (
          <Button
            variant='outlined'
            color='primary'
            size='small'
            onClick={() => openRecipientsModal(row)}
          >
            {intl.formatMessage({ id: 'base.view', defaultMessage: 'View' })} ({row.reports.length})
          </Button>
        )
      }
    ],
    [intl, isColumnHidden, openDataModal, openRecipientsModal]
  );

  const sortParam = React.useMemo(() => {
    if (sortColumnIndex === undefined || !sortDirection) {
      return undefined;
    }

    const sortColumn = columns[sortColumnIndex];
    if (!sortColumn || typeof sortColumn.sortField !== 'string' || !sortColumn.sortField) {
      return undefined;
    }

    if (sortDirection === 'desc') {
      return `-${sortColumn.sortField}`;
    }

    return sortColumn.sortField;
  }, [columns, sortColumnIndex, sortDirection]);

  const fetchReports = React.useCallback(() => {
    return getReports({
      limit: pageSize,
      skip: page * pageSize,
      sort: sortParam,
      date: selectedMonth.from.format('YYYY-MM-DD'),
      accountId: accountIdFilterParam,
      accountName: accountNamesFilterParam,
      partner: partnersFilterParam
    });
  }, [
    accountIdFilterParam,
    accountNamesFilterParam,
    getReports,
    page,
    pageSize,
    partnersFilterParam,
    selectedMonth.from,
    sortParam
  ]);

  const handleSendReport = React.useCallback(
    async (options: { accountId: string; reportId: MonthlyReport['id'] }) => {
      dispatchState({
        type: 'set_report_sending',
        payload: { reportId: options.reportId, isSending: true }
      });

      try {
        const response = await sendReportEmail(options.accountId, options.reportId);
        if (response) {
          showNotification(
            intl.formatMessage(
              {
                id: 'admin.monthlyReports.sendSuccess',
                defaultMessage: 'Report email sent to {count} recipients.'
              },
              { count: response.sent }
            )
          );
          await fetchReports();
        }
      } finally {
        dispatchState({
          type: 'set_report_sending',
          payload: { reportId: options.reportId, isSending: false }
        });
      }
    },
    [fetchReports, intl, sendReportEmail, showNotification]
  );

  const refreshSelectedRecipientsReport = React.useCallback(
    async (reportId: MonthlyReport['id']) => {
      const response = await fetchReports();

      if (!response) {
        return;
      }

      const refreshedReport = response.data.find((report) => report.id === reportId);
      if (refreshedReport) {
        dispatchState({ type: 'open_recipients_modal', payload: refreshedReport });
      }
    },
    [fetchReports]
  );

  const handleAddRecipient = React.useCallback(
    async (draft: Omit<MonthlyReportRecipient, 'id' | 'accountId' | 'userId'>) => {
      if (!selectedRecipientsReport?.account?.id) {
        return;
      }

      dispatchState({ type: 'set_recipients_mutation_pending', payload: true });

      try {
        await createRecipient(draft, selectedRecipientsReport.account.id);
        await refreshSelectedRecipientsReport(selectedRecipientsReport.id);
      } finally {
        dispatchState({ type: 'set_recipients_mutation_pending', payload: false });
      }
    },
    [createRecipient, refreshSelectedRecipientsReport, selectedRecipientsReport]
  );

  const handleDeleteRecipient = React.useCallback(
    async (recipientId: MonthlyReportRecipient['id']) => {
      if (!selectedRecipientsReport) {
        return;
      }

      dispatchState({ type: 'set_recipients_mutation_pending', payload: true });

      try {
        await deleteRecipient(recipientId, selectedRecipientsReport.account.id);
        await refreshSelectedRecipientsReport(selectedRecipientsReport.id);
      } finally {
        dispatchState({ type: 'set_recipients_mutation_pending', payload: false });
      }
    },
    [deleteRecipient, refreshSelectedRecipientsReport, selectedRecipientsReport]
  );

  React.useEffect(() => {
    void fetchReports();
  }, [fetchReports]);

  const selectedRecipientRows: MonthlyReportRow[] | null = selectedRecipientsReport
    ? selectedRecipientsReport.reports
    : null;

  return (
    <>
      <div className={classes.tableFilterRow}>
        <Button
          variant='outlined'
          size='small'
          className={classes.columnsButton}
          onClick={(event) =>
            dispatchState({ type: 'set_columns_menu_anchor', payload: event.currentTarget })
          }
        >
          Columns
        </Button>
        <Menu
          anchorEl={columnsMenuAnchor}
          open={Boolean(columnsMenuAnchor)}
          onClose={() => dispatchState({ type: 'set_columns_menu_anchor', payload: undefined })}
        >
          {COLLAPSIBLE_COLUMNS.map((column) => (
            <MenuItem
              key={column.key}
              onClick={() => {
                toggleColumn(column.key);
              }}
            >
              <Checkbox checked={!hiddenColumns.includes(column.key)} color='primary' />
              <ListItemText
                primary={intl.formatMessage({
                  id: column.labelId,
                  defaultMessage: column.defaultMessage
                })}
              />
            </MenuItem>
          ))}
        </Menu>
        <TextField
          label={intl.formatMessage({ id: 'partner', defaultMessage: 'Partner' })}
          variant='outlined'
          size='small'
          className={classes.partnerFilterInput}
          defaultValue=''
          onChange={(event) => {
            setDebouncedPartnerFilter(event.target.value);
          }}
        />
        <TextField
          label={intl.formatMessage({ id: 'admin.monthlyReports.accountName' })}
          variant='outlined'
          size='small'
          className={classes.accountNameFilterInput}
          defaultValue=''
          onChange={(event) => {
            setDebouncedAccountNameFilter(event.target.value);
          }}
        />
        <TextField
          label={intl.formatMessage({ id: 'accountId', defaultMessage: 'Account ID' })}
          variant='outlined'
          size='small'
          className={classes.accountFilterInput}
          defaultValue=''
          onChange={(event) => {
            setDebouncedAccountsFilter(event.target.value);
          }}
        />
        <DateInput
          periods={[CalendarPeriod.month]}
          isoWeek={true}
          size='medium'
          className={classes.monthFilterInput}
          onDateChange={(change) => dispatchState({ type: 'set_selected_month', payload: change })}
          selection={selectedMonth}
          period={selectedMonth.type}
        />
      </div>
      <AlteredMaterialTable
        fullWidth
        columns={columns}
        data={reports}
        page={page}
        totalCount={totalReports}
        isLoading={reportsStatus === 'loading'}
        onChangePage={(nextPage: number) => dispatchState({ type: 'set_page', payload: nextPage })}
        onChangeRowsPerPage={(nextPageSize: number) =>
          dispatchState({ type: 'set_page_size', payload: nextPageSize })
        }
        onOrderChange={(columnIndex: number, direction: 'asc' | 'desc') =>
          dispatchState({
            type: 'set_sort',
            payload: {
              columnIndex: columnIndex >= 0 ? columnIndex : undefined,
              direction
            }
          })
        }
        options={{ ...materialTableOptions, pageSize }}
        noContentMessage={intl.formatMessage({ id: 'admin.monthlyReports.empty' })}
        style={{
          border: `1px solid ${colors.grey['200']}`,
          overflow: 'hidden'
        }}
      />
      {selectedDataReport ? (
        <DataModal open data={selectedDataReport.data} onClose={closeDataModal} />
      ) : null}
      <RecipientsModal
        open={!!selectedRecipientsReport}
        reportRows={selectedRecipientRows}
        accountId={selectedRecipientsReport?.account?.id}
        isRecipientsMutationPending={recipientsMutationPending}
        onAddRecipient={handleAddRecipient}
        onDeleteRecipient={handleDeleteRecipient}
        onClose={closeRecipientsModal}
        onSend={() => {
          const accountId = selectedRecipientsReport?.account?.id;
          if (selectedRecipientsReport && accountId) {
            void handleSendReport({ accountId, reportId: selectedRecipientsReport.id });
          }
        }}
        isSending={
          selectedRecipientsReport ? sendingReportIds.includes(selectedRecipientsReport.id) : false
        }
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  hiddenColumns: selectMonthlyReportsHiddenColumns(state),
  reports: selectMonthlyReportsData(state),
  reportsStatus: selectMonthlyReportsStatus(state),
  totalReports: selectMonthlyReportsTotal(state)
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<
    unknown,
    unknown,
    MonthlyReportsActions | ErrorActions | NotificationActions | MonthlyReportRecipientActions
  >
) => ({
  getReports: (params: MonthlyReportsQueryParams) =>
    dispatch(monthlyReportsDispatch.getReports(params)),
  sendReportEmail: (accountId: string, reportId: MonthlyReport['id']) =>
    dispatch(monthlyReportsDispatch.sendReportEmail(accountId, reportId)),
  createRecipient: (
    recipient: Omit<MonthlyReportRecipient, 'id' | 'accountId' | 'userId'>,
    accountId: string
  ) => dispatch(monthlyReportRecipientsDispatch.createAdminRecipient(recipient, accountId)),
  deleteRecipient: (recipientId: MonthlyReportRecipient['id'], accountId: string) =>
    dispatch(monthlyReportRecipientsDispatch.deleteAdminRecipient(recipientId, accountId)),
  setHiddenColumns: (columns: string[]) =>
    dispatch(monthlyReportsDispatch.setHiddenColumns(columns)),
  showNotification: (message: string) => dispatch(notificationDispatch.showNotification(message))
});

export default connect<StateProps, DispatchProps, unknown>(
  mapStateToProps,
  mapDispatchToProps
)(MonthlyReportsTable);
